import React from "react";
import { Link } from "react-router-dom";

/* Shared atomic UI primitives — the same surface the static prototype used. */

export const Pill = ({ children, tone = "default", className = "" }) => {
  const map = {
    default: "border-white/10 bg-white/[0.04] text-white/80",
    accent: "border-cyan-300/25 bg-cyan-300/[0.06] text-cyan-200",
    amber: "border-amber-300/25 bg-amber-300/[0.06] text-amber-200",
    danger: "border-rose-300/25 bg-rose-300/[0.06] text-rose-200",
  };
  return (
    <span className={`inline-flex items-center gap-2 rounded-full border ${map[tone]} px-3 py-1 text-[11px] font-medium uppercase tracking-[0.14em] ${className}`}>
      {children}
    </span>
  );
};

export const LiveDot = ({ tone = "cyan" }) => {
  const colors = {
    cyan: "bg-cyan-300",
    amber: "bg-amber-300",
    rose: "bg-rose-400",
    blue: "bg-blue-400",
  };
  return (
    <span className="relative inline-flex h-2 w-2">
      <span className={`live-dot absolute inline-flex h-full w-full rounded-full ${colors[tone]} opacity-70`} />
      <span className={`relative inline-flex h-2 w-2 rounded-full ${colors[tone]}`} />
    </span>
  );
};

export const SectionLabel = ({ children, tone = "default" }) => (
  <div className="flex items-center gap-3">
    <span className="h-px w-8 bg-gradient-to-r from-transparent via-white/30 to-white/0" />
    <Pill tone={tone}>{children}</Pill>
  </div>
);

export const ButtonPrimary = ({ children, href = "#", to, onClick, className = "", external = false }) => {
  const cls = `group inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-[15px] font-semibold text-[#051030] shadow-[0_18px_60px_-15px_rgba(94,234,212,0.6)] hover:-translate-y-0.5 hover:shadow-[0_22px_70px_-15px_rgba(94,234,212,0.85)] ${className}`;
  const arrow = (
    <svg className="h-4 w-4 transition-transform group-hover:translate-x-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  if (to && !external) return <Link to={to} className={cls} onClick={onClick}>{children}{arrow}</Link>;
  return <a href={href} onClick={onClick} className={cls} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined}>{children}{arrow}</a>;
};

export const ButtonGhost = ({ children, href = "#", to, onClick, className = "", external = false }) => {
  const cls = `inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-6 py-3.5 text-[15px] font-semibold text-white/90 backdrop-blur hover:border-white/30 hover:bg-white/[0.07] ${className}`;
  if (to && !external) return <Link to={to} className={cls} onClick={onClick}>{children}</Link>;
  return <a href={href} onClick={onClick} className={cls} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined}>{children}</a>;
};

export const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-white/[0.08] bg-white/[0.025] ${className}`}>{children}</div>
);

/* Wordmark — colored mark from PNG + crisp white text. Links to "/". */
export const Wordmark = ({ className = "", size = 30, dark = true }) => (
  <Link to="/" className={`inline-flex items-center gap-2.5 ${className}`} aria-label="EkkBaz AI — home">
    <img src="/assets/images/ekkbaz-mark.png" alt="" className="block w-auto" style={{ height: size + "px" }} />
    <span
      className="leading-none tracking-tight font-semibold"
      style={{
        fontSize: Math.round(size * 0.7) + "px",
        color: dark ? "#ffffff" : "#0b1535",
        letterSpacing: "-0.01em",
      }}
    >
      EkkBaz <span style={{ color: dark ? "#5eead4" : "#2563eb" }}>AI</span>
    </span>
  </Link>
);

export const Tick = () => (
  <svg className="h-3.5 w-3.5 text-cyan-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6">
    <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const Arrow = ({ size = 4 }) => (
  <svg className={`h-${size} w-${size}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
    <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
